import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { resetPassword, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/site/Logo";

export default function NulstilAdgangskode() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) {
            setError("Manglende reset-token. Bed om et nyt link.");
        }
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 8) {
            setError("Adgangskoden skal være mindst 8 tegn.");
            return;
        }
        if (password !== confirm) {
            setError("Adgangskoderne matcher ikke.");
            return;
        }
        setLoading(true);
        try {
            await resetPassword(token, password);
            setDone(true);
            setTimeout(() => navigate("/login", { replace: true }), 2500);
        } catch (err) {
            setError(formatApiError(err) || "Kunne ikke nulstille adgangskoden.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/40 px-4 py-12">
            <div className="w-full max-w-md">
                <div className="flex justify-center mb-8">
                    <Logo />
                </div>
                <div className="bg-white rounded-2xl shadow-xl shadow-slate-900/5 border border-slate-200/70 p-7" data-testid="reset-password-card">
                    <h1 className="text-2xl font-bold text-slate-900 mb-1">Vælg ny adgangskode</h1>
                    <p className="text-sm text-slate-500 mb-6">
                        Adgangskoden skal være mindst 8 tegn.
                    </p>

                    {done ? (
                        <div className="text-center py-6" data-testid="reset-password-success">
                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-4">
                                <CheckCircle2 className="w-7 h-7" />
                            </div>
                            <h2 className="text-lg font-semibold text-slate-900 mb-2">Adgangskoden er opdateret</h2>
                            <p className="text-sm text-slate-600">Du bliver sendt til login-siden…</p>
                        </div>
                    ) : !token ? (
                        <div className="text-center py-6" data-testid="reset-password-no-token">
                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 text-amber-600 mb-4">
                                <AlertTriangle className="w-7 h-7" />
                            </div>
                            <p className="text-sm text-slate-600 mb-4">Reset-linket er ugyldigt eller mangler.</p>
                            <Link
                                to="/glemt-adgangskode"
                                data-testid="reset-password-request-new"
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Bed om et nyt link
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <div className="space-y-1">
                                <Label htmlFor="new-pw" className="text-xs font-medium text-slate-700">Ny adgangskode</Label>
                                <div className="relative">
                                    <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <Input
                                        id="new-pw"
                                        type="password"
                                        required
                                        minLength={8}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        data-testid="reset-password-new-input"
                                        className="h-11 pl-9 bg-slate-50/60 border-slate-200"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="confirm-pw" className="text-xs font-medium text-slate-700">Gentag ny adgangskode</Label>
                                <div className="relative">
                                    <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <Input
                                        id="confirm-pw"
                                        type="password"
                                        required
                                        minLength={8}
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        data-testid="reset-password-confirm-input"
                                        className="h-11 pl-9 bg-slate-50/60 border-slate-200"
                                    />
                                </div>
                            </div>
                            {error && (
                                <div data-testid="reset-password-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                    {error}
                                </div>
                            )}
                            <Button
                                type="submit"
                                disabled={loading}
                                data-testid="reset-password-submit-btn"
                                className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Nulstil adgangskode
                            </Button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
